import { ImgID } from "../../image_loader/img_id.js";
import { ColorScheme } from "../common_ui_elements/color_scheme.js";
import { TextStyleScheme } from "../common_ui_elements/text_style_scheme.js";
import { UIImage } from "../common_ui_elements/ui_image.js";
import { UINineSliceElement } from "../common_ui_elements/ui_nine_slice_element.js";
import { MyTextAlign, MyTextVerticalAlign, UITextElement } from "../common_ui_elements/ui_text_element.js";
import { AbstractDialog } from "./abstract_dialog.js";
import { DialogManager } from "./dialog_manager.js";
import { ScreenManager, Screens } from "../../screen_manager/screen_manager.js";
import { StatisticsStorage } from "../../storage/statistics_storage.js";
import { MoreLivesDialog } from "./more_lives_dialog.js";
import { TransformScale } from "../../transforms/transform_scale.js";
import { Easing } from "../../transforms/transform_base.js";
import { Vector2 } from "../../common/vector2.js";
import { pulseCannons } from "../common_ui_elements/cannon_pulse.js";
import { OverlayFactory } from "../overlay/overlay_factory.js";
import { OverlayId } from "../overlay/overlay_ids.js";
// Safe at runtime: level_pack_service only `import type`s from level_config, so
// this adds no edge back into that cycle.
import { LevelPackService } from "../../level_pack/level_pack_service.js";
import { Game } from "../../game.js";
import {
  BuyBoosterDialog,
  BuyBoosterType,
  getBoosterTextVariableParams,
  getDefaultBoosterEffectValue,
} from "./buy_booster_dialog.js";
import { CumulativeBoosterID } from "../../cumulative_boosters/cumulative_booster_id.js";
import { CumulativeBoosterService } from "../../cumulative_boosters/cumulative_booster_service.js";
import { UIElementHolder } from "../common_ui_elements/ui_element_holder.js";
import { LocalizationManager } from "../../localization/localization_manager.js";

export enum PlayDialogState
{
  Play,
  TryAgain,
}

export enum PlayDialogDifficulty
{
  Common,
  Hard,
  SuperHard,
}

interface LevelStartBoosterSlotView
{
  background: UIImage;
  badge: UIImage;
  badge_size: number;
  count_text: UITextElement | null;
}

export class PlayDialog extends AbstractDialog
{
  private static readonly PASSIVE_BOOSTER_IDS = [
    CumulativeBoosterID.Mine,
  ] as const;
  private static readonly LOCK_ICON_SCALE = 0.75;
  private static readonly SLOT_ICON_Y = -22;
  private static readonly DESIGN_W = 961.26;
  private static readonly DESIGN_H = 1335;
  private static readonly BG_W = 909;
  private static readonly BG_H = 1114;
  private static readonly BG_Y = 332;
  private readonly level: number;
  private readonly state: PlayDialogState;
  private readonly difficulty: PlayDialogDifficulty;
  private readonly on_close?: () => void;
  private readonly on_retry?: () => void | Promise<void>;
  private is_retrying = false;
  private retry_inflight = false;
  private close_btn!: UIImage;
  private top_img!: UIImage;
  private booster_tooltip: UIElementHolder | null = null;
  private booster_tooltip_dismiss_layer: UIElementHolder | null = null;
  private selected_level_start_booster_id: CumulativeBoosterID | null = null;
  private readonly level_start_booster_slots = new Map<CumulativeBoosterID, LevelStartBoosterSlotView>();

  constructor(
    level: number,
    state: PlayDialogState = PlayDialogState.Play,
    difficulty: PlayDialogDifficulty = PlayDialogDifficulty.Common,
    on_close?: () => void,
    on_retry?: () => void | Promise<void>
  )
  {
    const body_w = LayoutScaler.widthFromDesign( PlayDialog.DESIGN_W );
    const body_h = LayoutScaler.heightFromDesign( PlayDialog.DESIGN_H );

    const body_x = ( DialogManager.getWidth() - body_w ) / 2;
    const body_y = ( DialogManager.getHeight() - body_h ) / 2
      - LayoutScaler.heightFromDesign( 140 );

    super( DialogManager.ctx, body_x, body_y, body_w, body_h );

    this.level = level;
    this.state = state;
    this.difficulty = difficulty;
    this.on_close = on_close;
    this.on_retry = on_retry;

    this.createBackground();
    this.createTopImage();
    this.createTopBanner();
    this.createDifficultyLabel();
    this.createInfoFrame();
    this.createPlayButton();
    this.createCloseButton();
  }

  private createBackground(): void
  {
    const design_bg_x = ( PlayDialog.DESIGN_W - PlayDialog.BG_W ) / 2;

    const bg = new UINineSliceElement(
      this.ctx,
      LayoutScaler.xFromDesign( design_bg_x ),
      LayoutScaler.yFromDesign( PlayDialog.BG_Y ),
      LayoutScaler.widthFromDesign( PlayDialog.BG_W ),
      LayoutScaler.heightFromDesign( PlayDialog.BG_H ),
      this.getBackgroundImgId(),
      "capsule_bg"
    );
    this.addChild( bg );
  }

  private getBackgroundImgId(): ImgID
  {
    switch ( this.difficulty )
    {
      case PlayDialogDifficulty.Hard:      return ImgID.UI_CapsuleBluePurple;
      case PlayDialogDifficulty.SuperHard: return ImgID.UI_CapsuleBlueRed;
      default:                             return ImgID.UI_CapsuleBlue;
    }
  }

  private createDifficultyLabel(): void
  {
    if ( this.difficulty === PlayDialogDifficulty.Common )
      return;

    const difficulty_img_w = LayoutScaler.widthFromDesign( 555 );
    const difficulty_img_h = LayoutScaler.heightFromDesign( 125 );
    const difficulty_img_x = ( this.rect.getW() - difficulty_img_w ) / 2;
    const difficulty_img_y = LayoutScaler.yFromDesign( 142 );

    const difficulty_img = new UIImage(
      this.ctx,
      difficulty_img_x,
      difficulty_img_y,
      difficulty_img_w,
      difficulty_img_h,
      ImgID.UI_TopBannerHeader
    );
    this.addChild( difficulty_img );

    const is_super_hard = this.difficulty === PlayDialogDifficulty.SuperHard;
    const design_label_x = 0;
    const design_label_y = 40;
    const design_label_h = 83;
    const design_font_size = 70;
    const design_outline_w = 14;

    const label = new UITextElement(
      this.ctx,
      LayoutScaler.xFromDesign( design_label_x ),
      LayoutScaler.yFromDesign( design_label_y ),
      difficulty_img_w,
      LayoutScaler.heightFromDesign( design_label_h ),
      {
        text: this.difficulty === PlayDialogDifficulty.SuperHard
          ? "Super Hard!"
          : this.difficulty === PlayDialogDifficulty.Hard
            ? "Hard!"
            : "Normal",
        font_size: LayoutScaler.textSizeFromDesign( design_font_size ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.rubik,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        outline_width: LayoutScaler.textBorderSizeFromDesign( design_outline_w ),
        outline_color: ColorScheme.fireworks_dialog_title_outline,
        scale_to_width: true,
        auto_wrap: false,
      }
    );
    difficulty_img.addChild( label );
  }

  private createTopBanner(): void
  {
    const design_banner_y = 184;
    const design_banner_h = 369;
    const design_banner_w = 1066;
    const design_title_x = 222;
    const design_title_y = 76;
    const design_title_w = 518;
    const design_title_h = 148;
    const design_font_size = 88;
    const design_outline_w = 14;

    const banner_w = LayoutScaler.widthFromDesign( design_banner_w );
    const banner_h = LayoutScaler.heightFromDesign( design_banner_h );

    const banner_x = this.rect.getW() / 2 - banner_w / 2 - LayoutScaler.xFromDesign( 2 );

    const img = new UIImage(
      this.ctx,
      banner_x,
      LayoutScaler.yFromDesign( design_banner_y ),
      banner_w,
      banner_h,
      ImgID.UI_TopBanner
    );
    this.addChild( img );

    const title_h = LayoutScaler.heightFromDesign( design_title_h );
    const title_w = LayoutScaler.widthFromDesign( design_title_w );

    const title = new UITextElement(
      this.ctx,
      0,
      LayoutScaler.yFromDesign( design_title_y ),
      banner_w,
      title_h,
      {
        localization_key: "PLAY_DIALOG.TITLE",
        text_variable_params: [ this.level.toString() ],
        font_size: LayoutScaler.textSizeFromDesign( design_font_size ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.rubik,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        outline_width: LayoutScaler.textBorderSizeFromDesign( design_outline_w ),
        outline_color: ColorScheme.fireworks_dialog_title_outline,
        scale_to_width: true,
        auto_wrap: false,
      }
    );
    img.addChild( title );
  }

  private createTopImage(): void
  {
    const design_img_y = -80;
    const design_img_w = 802;
    const design_img_h = 383;
    const design_img_x = 60;

    const img = new UIImage(
      this.ctx,
      LayoutScaler.xFromDesign( design_img_x ),
      LayoutScaler.yFromDesign( design_img_y ),
      LayoutScaler.widthFromDesign( design_img_w ),
      LayoutScaler.heightFromDesign( design_img_h ),
      ImgID.UI_PlayDialogTop
    );
    this.addChild( img );
    this.top_img = img;
  }

  private createInfoFrame(): void
  {
    // Match the native 730x587 art so the left and bottom edges are not
    // squeezed relative to the checker pattern and border.
    const design_frame_w = 780;
    const frame_top_extension = 16;
    const design_frame_h = 480 + frame_top_extension;
    const design_frame_x = ( PlayDialog.DESIGN_W - design_frame_w ) / 2;
    const design_frame_y = 559.44 - frame_top_extension;

    const frame_w = LayoutScaler.widthFromDesign( design_frame_w );
    const frame_h = LayoutScaler.heightFromDesign( design_frame_h );
    const frame_x = LayoutScaler.xFromDesign( design_frame_x );
    const frame_y = LayoutScaler.yFromDesign( design_frame_y );

    const frame = new UIImage( this.ctx, frame_x, frame_y, frame_w, frame_h, ImgID.UI_GameStartContentBg );
    this.addChild( frame );

    // const title_w = 360;
    // const title_h = 100;
    // const title = new UIImage(
    //   this.ctx,
    //   LayoutScaler.xFromDesign( ( design_frame_w - title_w ) / 2 ),
    //   LayoutScaler.yFromDesign( -58 + frame_top_extension ),
    //   LayoutScaler.widthFromDesign( title_w ),
    //   LayoutScaler.heightFromDesign( title_h ),
    //   ImgID.UI_GameStartBoosterTitle
    // );
    // frame.addChild( title );

    // const text = new UITextElement(
    //   this.ctx, 0, 0,
    //   LayoutScaler.widthFromDesign( title_w ),
    //   LayoutScaler.heightFromDesign( title_h ),
    //   {
    //     text: "Booster:",
    //     font_size: LayoutScaler.textSizeFromDesign( 56 ),
    //     font_weight: 900,
    //     text_color: ColorScheme.white,
    //     text_style: TextStyleScheme.rubik,
    //     text_align: MyTextAlign.Center,
    //     vertical_align: MyTextVerticalAlign.Center,
    //     outline_width: LayoutScaler.textBorderSizeFromDesign( 14 ),
    //     outline_color: "#2A5D97",
    //     scale_to_width: true,
    //     auto_wrap: false,
    //   }
    // );
    // title.addChild( text );

    const slot_size = 195;
    const slot_gap = 128;
    const slots_width = slot_size * PlayDialog.PASSIVE_BOOSTER_IDS.length
      + slot_gap * ( PlayDialog.PASSIVE_BOOSTER_IDS.length - 1 );
    const first_slot_x = ( design_frame_w - slots_width ) / 2;
    const centered_y = ( design_frame_h - slot_size ) / 2;
    PlayDialog.PASSIVE_BOOSTER_IDS.forEach( ( booster_id, index ) =>
      this.createLevelStartBoosterSlot(
        frame,
        first_slot_x + index * ( slot_size + slot_gap ),
        centered_y,
        slot_size,
        booster_id
      )
    );
  }

  private createLevelStartBoosterSlot(
    frame: UIImage,
    x: number,
    y: number,
    size: number,
    booster_id: CumulativeBoosterID
  ): void
  {
    const unlock_level = CumulativeBoosterService.getUnlockLevel( booster_id );
    const is_unlocked = CumulativeBoosterService.isUnlocked( booster_id, StatisticsStorage.load().level );
    const slot = new UIImage(
      this.ctx,
      LayoutScaler.xFromDesign( x ),
      LayoutScaler.yFromDesign( y ),
      LayoutScaler.widthFromDesign( size ),
      LayoutScaler.heightFromDesign( size ),
      ImgID.UI_PassiveBoosterDisabled,
      undefined,
      true
    );
    frame.addChild( slot );

    if ( !is_unlocked )
    {
      const lock_size = size * PlayDialog.LOCK_ICON_SCALE;
      const lock_x = LayoutScaler.widthFromDesign( ( size - lock_size ) / 2 );
      const lock_y = LayoutScaler.yFromDesign( PlayDialog.SLOT_ICON_Y ) + LayoutScaler.heightFromDesign( ( size - lock_size ) / 2 );
      const lock_w = LayoutScaler.widthFromDesign( lock_size );
      const lock_h = LayoutScaler.heightFromDesign( lock_size );
      const locked_icon = new UIImage( this.ctx, lock_x, lock_y, lock_w, lock_h, ImgID.IconNavigationLock );
      slot.addChild( locked_icon, true );
      this.createLockedLevelLabel( slot, size, unlock_level );
      slot.setOnClick( () => this.showBoosterTooltip(
        slot,
        `${LocalizationManager.t( "BOOSTER.AVAILABLE" )} ${unlock_level}`
      ) );
      return;
    }

    const icon_design_size = 190;
    const icon_id = ImgID.UI_PassiveBoosterMine;
    const icon = new UIImage(
      this.ctx,
      LayoutScaler.xFromDesign( ( size - icon_design_size ) / 2 ),
      LayoutScaler.yFromDesign( -12 ),
      LayoutScaler.widthFromDesign( icon_design_size ),
      LayoutScaler.heightFromDesign( icon_design_size ),
      icon_id
    );
    slot.addChild( icon, true );

    const badge_design_size = 80;
    const badge_size = LayoutScaler.widthFromDesign( badge_design_size );
    const badge = new UIImage(
      this.ctx,
      LayoutScaler.xFromDesign( ( size - badge_design_size ) / 2 ),
      LayoutScaler.yFromDesign( 145 ),
      badge_size,
      LayoutScaler.heightFromDesign( badge_design_size ),
      ImgID.UI_PassiveBoosterCountBadge,
      undefined,
      true
    );
    slot.addChild( badge, true );

    const view: LevelStartBoosterSlotView = {
      background: slot,
      badge,
      badge_size,
      count_text: null,
    };
    this.level_start_booster_slots.set( booster_id, view );
    this.refreshLevelStartBoosterSelection();

    slot.setOnClick( () =>
    {
      if ( CumulativeBoosterService.inventory.getCount( booster_id ) <= 0 )
      {
        void this.openBuyBoosterDialog(
          BuyBoosterType.Firework,
          booster_id,
          () => this.refreshLevelStartBoosterSelection()
        );
        return;
      }
      this.selected_level_start_booster_id = this.selected_level_start_booster_id === booster_id
        ? null
        : booster_id;
      this.refreshLevelStartBoosterSelection();
    } );
  }

  private refreshLevelStartBoosterSelection(): void
  {
    for ( const [ id, view ] of this.level_start_booster_slots )
    {
      const count = CumulativeBoosterService.inventory.getCount( id );
      const selected = this.selected_level_start_booster_id === id && count > 0;

      view.background.setImageId(
        selected ? ImgID.UI_PassiveBoosterEnabled : ImgID.UI_PassiveBoosterDisabled
      );
      view.badge.setImageId(
        selected
          ? ImgID.UI_PassiveBoosterSelectedBadge
          : count > 0
            ? ImgID.UI_PassiveBoosterCountBadge
            : ImgID.UI_PassiveBoosterPlusBadge
      );

      if ( view.count_text )
      {
        view.badge.removeChild( view.count_text );
        view.count_text = null;
      }
      if ( count > 0 && !selected )
        view.count_text = this.createBoosterCountText( view.badge, view.badge_size, count );
    }
    this.invalidateSubtree();
  }

  private createConfiguredBoosterSlot(
    frame: UIImage,
    x: number,
    y: number,
    size: number,
    icon_id: ImgID,
    booster_type: BuyBoosterType,
    booster_id: CumulativeBoosterID
  ): void
  {
    const unlock_level = CumulativeBoosterService.getUnlockLevel( booster_id );
    const is_unlocked = CumulativeBoosterService.isUnlocked(
      booster_id,
      StatisticsStorage.load().level
    );
    const slot = this.createBoosterSlot(
      frame,
      x,
      y,
      size,
      is_unlocked ? icon_id : ImgID.IconNavigationLock
    );

    if ( is_unlocked )
      this.createBoosterBadge( slot, size, booster_type, booster_id );
    else
    {
      this.createLockedLevelLabel( slot, size, unlock_level );
      slot.setOnClick( () => this.showBoosterTooltip(
        slot,
        `${LocalizationManager.t( "BOOSTER.AVAILABLE" )} ${unlock_level}`
      ) );
    }
  }

  private createBoosterSlot( frame: UIImage, x: number, y: number, size: number, icon_id: ImgID ): UIImage
  {
    const slot = new UIImage(
      this.ctx,
      LayoutScaler.xFromDesign( x ), LayoutScaler.yFromDesign( y ),
      LayoutScaler.widthFromDesign( size ), LayoutScaler.heightFromDesign( size ),
      ImgID.UI_GameStartBoosterBg, undefined, true
    );
    frame.addChild( slot );

    const icon_size = icon_id === ImgID.IconNavigationLock ? size * PlayDialog.LOCK_ICON_SCALE : size;
    const icon_x = LayoutScaler.widthFromDesign( ( size - icon_size ) / 2 );
    const icon_y = LayoutScaler.yFromDesign( PlayDialog.SLOT_ICON_Y ) + LayoutScaler.heightFromDesign( ( size - icon_size ) / 2 );
    const icon_w = LayoutScaler.widthFromDesign( icon_size );
    const icon_h = LayoutScaler.heightFromDesign( icon_size );
    const icon = new UIImage( this.ctx, icon_x, icon_y, icon_w, icon_h, icon_id );
    slot.addChild( icon, true );
    return slot;
  }

  private async openBuyBoosterDialog(
    booster_type: BuyBoosterType,
    booster_id: CumulativeBoosterID,
    on_inventory_changed?: () => void
  ): Promise<void>
  {
    const offer = await CumulativeBoosterService.getOrLoadFirstPriceOffer( booster_id );
    if ( offer === null )
    {
      console.warn( `[Boosters] No price offer for booster ${booster_id}` );
      return;
    }

    DialogManager.openDialog( new BuyBoosterDialog( {
      booster_type,
      booster_id,
      offer,
      source: "game_start",
      on_buy: () => on_inventory_changed?.(),
      on_reward: () => on_inventory_changed?.(),
    } ) );
  }

  private createLockedLevelLabel(
    booster_bg: UIImage,
    design_slot_size: number,
    unlock_level: number
  ): void
  {
    const label = new UITextElement(
      this.ctx, 0, LayoutScaler.yFromDesign( design_slot_size - 28 ),
      LayoutScaler.widthFromDesign( design_slot_size ), LayoutScaler.heightFromDesign( 50 ),
      {
        text: `Lv.${unlock_level}`, font_size: LayoutScaler.textSizeFromDesign( 38 ), font_weight: 900,
        text_color: ColorScheme.white, text_style: TextStyleScheme.rubik,
        text_align: MyTextAlign.Center, vertical_align: MyTextVerticalAlign.Center,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 8 ),
        outline_color: ColorScheme.tooltip_text_outline,
      }
    );
    booster_bg.addChild( label, true );
  }

  private createBoosterBadge(
    booster_bg: UIImage,
    design_slot_size: number,
    booster_type: BuyBoosterType,
    booster_id: CumulativeBoosterID
  ): void
  {
    const design_badge_size = 80;
    const badge_size = LayoutScaler.widthFromDesign( design_badge_size );
    const badge_x = LayoutScaler.xFromDesign( ( design_slot_size - design_badge_size ) / 2 );
    const badge_y = LayoutScaler.yFromDesign( design_slot_size - design_badge_size / 2 );

    let count = CumulativeBoosterService.inventory.getCount( booster_id );

    const badge = new UIImage(
      this.ctx, badge_x, badge_y, badge_size, badge_size,
      count > 0 ? ImgID.UI_GameStartRedCircle : ImgID.UI_PlusBtn,
      undefined,
      true
    );
    booster_bg.addChild( badge, true );

    let count_text: UITextElement | null = null;

    const refresh = (): void =>
    {
      count = CumulativeBoosterService.inventory.getCount( booster_id );
      if ( count_text )
      {
        badge.removeChild( count_text );
        count_text = null;
      }

      if ( count > 0 )
      {
        badge.setImageId( ImgID.UI_GameStartRedCircle );
        count_text = this.createBoosterCountText( badge, badge_size, count );
      }
      else
      {
        badge.setImageId( ImgID.UI_PlusBtn );
      }
    };

    refresh();

    booster_bg.setOnClick( () =>
    {
      count = CumulativeBoosterService.inventory.getCount( booster_id );
      if ( count === 0 )
      {
        void this.openBuyBoosterDialog( booster_type, booster_id, refresh );
        return;
      }

      this.showBoosterTooltip(
        booster_bg,
        LocalizationManager.tWithParams(
          this.getBoosterTitleKey( booster_type ),
          getBoosterTextVariableParams(
            booster_type,
            this.getConfiguredBoosterEffectValue( booster_id, booster_type )
          )
        )
      );
    } );
  }

  private getConfiguredBoosterEffectValue(
    booster_id: CumulativeBoosterID,
    booster_type: BuyBoosterType
  ): number | null
  {
    switch ( booster_id )
    {
      case CumulativeBoosterID.Multiball:
        return CumulativeBoosterService.getBoosterProperties(
          CumulativeBoosterID.Multiball
        )?.ball_count ?? getDefaultBoosterEffectValue( booster_type );
      case CumulativeBoosterID.InfiniteBalls:
        return CumulativeBoosterService.getBoosterProperties(
          CumulativeBoosterID.InfiniteBalls
        )?.duration_seconds ?? getDefaultBoosterEffectValue( booster_type );
      default:
        return getDefaultBoosterEffectValue( booster_type );
    }
  }

  private getBoosterTitleKey( booster_type: BuyBoosterType ): string
  {
    switch ( booster_type )
    {
      case BuyBoosterType.Firework:      return "BOOSTER.FIREWORK";
      case BuyBoosterType.FiveBalls:     return "BOOSTER.FIVE_BALLS";
      case BuyBoosterType.BigBall:       return "BOOSTER.BIG_BALL";
      case BuyBoosterType.InfiniteShots: return "BOOSTER.INFINITE_SHOTS";
    }
  }

  private showBoosterTooltip( booster_bg: UIImage, tooltip_text: string ): void
  {
    this.hideBoosterTooltip();

    const tape_w = LayoutScaler.widthFromDesign( 675 );
    const tape_h = LayoutScaler.heightFromDesign( 200 );
    const bottom_w = LayoutScaler.widthFromDesign( 110.46 );
    const bottom_h = LayoutScaler.heightFromDesign( 77.12 );
    const side_margin = LayoutScaler.widthFromDesign( 33 );
    const dialog_rect = this.getAbsoluteRect();
    const slot_rect = booster_bg.getAbsoluteRect();
    const slot_center_x = slot_rect.x - dialog_rect.x + slot_rect.width / 2;

    const tape_x = Math.max(
      side_margin,
      Math.min( slot_center_x - tape_w / 2, this.rect.getW() - side_margin - tape_w )
    );
    const tape_y = slot_rect.y - dialog_rect.y - LayoutScaler.heightFromDesign( 219.75 );
    const bottom_x = Math.max(
      0,
      Math.min( slot_center_x - tape_x - bottom_w / 2, tape_w - bottom_w )
    );
    const bottom_y = LayoutScaler.heightFromDesign( 167.68 );

    const dismiss_layer = new UIElementHolder(
      this.ctx,
      0,
      0,
      this.rect.getW(),
      this.rect.getH()
    );
    dismiss_layer.setOnClick( () => this.hideBoosterTooltip(), true );

    const tooltip = new UIElementHolder(
      this.ctx,
      tape_x,
      tape_y,
      tape_w,
      Math.max( tape_h, bottom_y + bottom_h )
    );
    tooltip.setClickable( false );

    const tape = new UINineSliceElement(
      this.ctx,
      0,
      0,
      tape_w,
      tape_h,
      ImgID.UI_GameBoosterLockedTape,
      "booster_tape"
    );
    tooltip.addChild( tape );

    const text = new UITextElement(
      this.ctx,
      LayoutScaler.xFromDesign( 30 ),
      LayoutScaler.yFromDesign( 20 ),
      tape_w - LayoutScaler.widthFromDesign( 60 ),
      tape_h - LayoutScaler.heightFromDesign( 40 ),
      {
        text: tooltip_text,
        font_size: LayoutScaler.textSizeFromDesign( 52 ),
        font_weight: 900,
        text_color: ColorScheme.tooltip_text_outline,
        text_style: TextStyleScheme.rubik,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 10 ),
        outline_color: ColorScheme.white,
        fit_font_size: true,
        min_font_size: LayoutScaler.textSizeFromDesign( 30 ),
        line_height: 0.95,
        auto_wrap: true,
        shadow_offset_x: 0,
        shadow_offset_y: 0
      }
    );
    tape.addChild( text );

    const bottom = new UIImage(
      this.ctx,
      bottom_x,
      bottom_y,
      bottom_w,
      bottom_h,
      ImgID.UI_GameBoosterBottom
    );
    tooltip.addChild( bottom );

    this.booster_tooltip_dismiss_layer = dismiss_layer;
    this.addChild( dismiss_layer );
    this.booster_tooltip = tooltip;
    this.addChild( tooltip );
    this.invalidateSubtree();
  }

  private hideBoosterTooltip(): void
  {
    if ( this.booster_tooltip )
      this.removeChild( this.booster_tooltip );
    if ( this.booster_tooltip_dismiss_layer )
      this.removeChild( this.booster_tooltip_dismiss_layer );

    this.booster_tooltip = null;
    this.booster_tooltip_dismiss_layer = null;
    this.invalidateSubtree();
  }

  private createBoosterCountText(
    badge: UIImage,
    badge_size: number,
    count: number
  ): UITextElement
  {
    const design_font_size = 40;
    const design_shadow_blur = 6;
    const design_shadow_offset_y = 3;

    const count_text = new UITextElement(
      this.ctx, 0, 0, badge_size, badge_size,
      {
        text: count.toString(),
        font_size: LayoutScaler.textSizeFromDesign( design_font_size ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.rubik,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        shadow_blur: LayoutScaler.widthFromDesign( design_shadow_blur ),
        shadow_color: ColorScheme.black_transparent,
        shadow_offset_y: LayoutScaler.yFromDesign( design_shadow_offset_y ),
        scale_to_width: true,
        auto_wrap: false,
      }
    );
    badge.addChild( count_text, true );
    return count_text;
  }

  private createPlayButton(): void
  {
    const design_btn_w = 700;
    const design_btn_h = 280;
    const design_btn_y = 1100;
    const design_btn_x = ( PlayDialog.DESIGN_W - design_btn_w ) / 2;

    const btn_w = LayoutScaler.widthFromDesign( design_btn_w );
    const btn_h = LayoutScaler.heightFromDesign( design_btn_h );
    const btn_x = LayoutScaler.xFromDesign( design_btn_x );
    const btn_y = LayoutScaler.yFromDesign( design_btn_y );

    const btn = new UIImage(
      this.ctx,
      btn_x,
      btn_y,
      btn_w,
      btn_h,
      ImgID.UI_GreenFrameBtn,
      undefined,
      true
    );

    const design_text_font_size = 100;

    const text_options = {
      font_size: LayoutScaler.textSizeFromDesign( design_text_font_size ),
      font_weight: 900,
      text_color: ColorScheme.white,
      text_style: TextStyleScheme.rubik,
      text_align: MyTextAlign.Center,
      vertical_align: MyTextVerticalAlign.Center,
      scale_to_width: true,
      auto_wrap: false,
      outline_width: LayoutScaler.textBorderSizeFromDesign( 10 ),
      outline_color: ColorScheme.menu_button_outline,
      ...TextStyleScheme.buttonShadow(),
    };

    const MAGIC_NUM_CORRECTION = 0.7;
    const play_text_w = btn_w * MAGIC_NUM_CORRECTION;
    const play_text_x = ( btn_w - play_text_w ) / 2;
    const is_try_again = this.state === PlayDialogState.TryAgain;
    const design_try_again_font_size = 70;
    const play_font_size = is_try_again ? LayoutScaler.textSizeFromDesign( design_try_again_font_size ) : text_options.font_size;

    const play_text = new UITextElement(
      this.ctx, play_text_x, 0, play_text_w, btn_h,
      {
        localization_key: is_try_again ? "TRY_AGAIN" : "PLAY",
        ...text_options,
        font_size: play_font_size,
        auto_wrap: is_try_again,
        scale_to_width: !is_try_again,
        line_height: is_try_again ? 0.9 : 1.1,
      }
    );
    btn.addChild( play_text, true );

    btn.setOnClick( async () =>
    {
      if ( this.retry_inflight )
        return;

      this.retry_inflight = true;
      this.is_retrying = true;

      const show_loading_screen = this.state === PlayDialogState.Play;
      if ( show_loading_screen )
        Game.instance.showLoadingGameScreen();
      DialogManager.closeAllDialogs();

      try
      {
        if ( show_loading_screen )
          await Game.instance.waitForLoadingGameScreenPaint();
        await this.on_retry?.();

        if ( this.state === PlayDialogState.TryAgain && StatisticsStorage.load().hearts <= 0 )
        {
          await ScreenManager.showScreen( Screens.MENU );
          DialogManager.openDialog( new MoreLivesDialog() );
          return;
        }
        if ( this.state === PlayDialogState.Play && StatisticsStorage.load().new_goal_pending )
        {
          StatisticsStorage.update( { new_goal_pending: false } );
          await ScreenManager.showScreen( Screens.GAME, {
            level: this.level,
            level_start_booster_id: this.selected_level_start_booster_id ?? undefined,
            defer_level_start_booster: true,
          } );
          OverlayFactory.openOverlay( OverlayId.NEW_GOAL_OVERLAY, {
            unlocks: LevelPackService.getUnlocks( this.level ),
            onClose: () =>
            {
              void ScreenManager.getCurrentScreenInstance()?.afterIntroOverlay?.();
            },
          } );
          return;
        }
        await ScreenManager.showScreen( Screens.GAME, {
          level: this.level,
          level_start_booster_id: this.selected_level_start_booster_id ?? undefined,
        } );
      }
      finally
      {
        if ( show_loading_screen )
        {
          await Game.instance.waitForMinimumLoadingGameScreenDuration();
          Game.instance.hideLoadingGameScreen();
        }
      }
    } );

    this.addChild( btn );
  }

  private createCloseButton(): void
  {
    const design_btn_size = 114;
    const design_btn_margin = 15;
    const design_btn_y = 191;
    const design_btn_x = PlayDialog.DESIGN_W - design_btn_size - design_btn_margin;

    const btn_w = LayoutScaler.widthFromDesign( design_btn_size );
    const btn_h = LayoutScaler.heightFromDesign( design_btn_size );

    const btn = new UIImage(
      this.ctx,
      LayoutScaler.xFromDesign( design_btn_x ),
      LayoutScaler.yFromDesign( design_btn_y ),
      btn_w,
      btn_h,
      ImgID.UI_SettingsDialogCloseBtn,
      undefined,
      true
    );
    btn.setOnClick( () => DialogManager.closeDialog() );
    this.addChild( btn );
    this.close_btn = btn;
  }

  protected override playAppearAnimation(
    from_scale      = 0.7,
    overshoot_scale = 1.1,
    final_scale     = 1.0,
    scale_up_duration = 136,
    settle_duration   = 96
  ): void
  {
    this.close_btn.setScale( new Vector2( 0, 0 ) );

    super.playAppearAnimation( from_scale, overshoot_scale, final_scale, scale_up_duration, settle_duration );

    this.popCloseButtonAfterAppear( this.close_btn, scale_up_duration + settle_duration );
    pulseCannons( this.top_img );
  }

  public override onDialogWillClose(): void
  {
    if ( this.is_retrying )
      return;

    this.on_close?.();
  }

  public name(): string
  {
    return "PlayDialog";
  }

  public render(): void
  {
    if ( !this.shouldBeRendered() )
      return;

    this.startRender();
    this.finishRender();
  }
}
